import type { ReactNode } from 'react';

interface SettingsCardProps {
  title: string;
  description?: string;
  children: ReactNode;
}

// The bordered section every settings tab is built from. Extracted the moment the second tab
// needed it — three tabs repeating the same heading/description/frame markup is exactly the
// duplication the shared-component rule exists to stop.
export function SettingsCard({ title, description, children }: SettingsCardProps) {
  return (
    <section className="mb-5 rounded border border-line bg-surface p-5 last:mb-0">
      <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
      {description && <p className="mt-1 text-[13px] leading-relaxed text-muted">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

interface ReadOnlyRowProps {
  label: string;
  value: ReactNode;
  // Why this can't be edited here. Rendered small under the value — a greyed field with no
  // explanation reads as broken rather than as deliberate.
  note?: string;
}

// A field the user can see but not change. Deliberately NOT a disabled <input>: a disabled input
// still looks like a control that might become editable, whereas this reads as a fact about the
// account — which is what a role or a federation is.
export function ReadOnlyRow({ label, value, note }: ReadOnlyRowProps) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-line py-2.5 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="w-full text-xs font-medium text-ink-3 sm:w-[160px] sm:flex-none">{label}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-ink">{value}</span>
        {note && <span className="mt-0.5 block text-[11px] text-muted-2">{note}</span>}
      </span>
    </div>
  );
}
